import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  Users, Plus, Truck, Phone, Mail, MapPin, CreditCard, Calendar as CalendarIcon,
  AlertTriangle, CheckCircle, Clock, Loader2, Edit, Trash2, MoreVertical, User,
  Link as LinkIcon, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const EQUIPMENT_TYPES = [
  { value: 'open_carrier', label: 'Open Carrier' },
  { value: 'enclosed_carrier', label: 'Enclosed Carrier' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'hotshot', label: 'Hotshot' },
  { value: 'dry_van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
];

const DriverManagementPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [invitingDriverId, setInvitingDriverId] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cdlExpiry, setCdlExpiry] = useState(null);
  const [medicalExpiry, setMedicalExpiry] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cdl_number: '',
    cdl_state: '',
    equipment_type: '',
    home_base: '',
    notes: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchDrivers();
    }
  }, [isAuthenticated]);

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/drivers');
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      cdl_number: '',
      cdl_state: '',
      equipment_type: '',
      home_base: '',
      notes: '',
    });
    setCdlExpiry(null);
    setMedicalExpiry(null);
    setEditingDriver(null);
  };

  const handleSaveDriver = async () => {
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      toast.error('Please fill in required fields (First Name, Last Name, Phone)');
      return;
    }

    setSaving(true);
    try {
      const driverData = {
        ...formData,
        cdl_expiry: cdlExpiry ? cdlExpiry.toISOString() : null,
        medical_card_expiry: medicalExpiry ? medicalExpiry.toISOString() : null,
      };

      if (editingDriver) {
        await api.put(`/drivers/${editingDriver.id}`, driverData);
        toast.success('Driver updated successfully');
      } else {
        await api.post('/drivers', driverData);
        toast.success('Driver added successfully');
      }

      setShowAddDialog(false);
      resetForm();
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleEditDriver = (driver) => {
    setEditingDriver(driver);
    setFormData({
      first_name: driver.first_name,
      last_name: driver.last_name,
      email: driver.email || '',
      phone: driver.phone,
      cdl_number: driver.cdl_number || '',
      cdl_state: driver.cdl_state || '',
      equipment_type: driver.equipment_type || '',
      home_base: driver.home_base || '',
      notes: driver.notes || '',
    });
    setCdlExpiry(driver.cdl_expiry ? new Date(driver.cdl_expiry) : null);
    setMedicalExpiry(driver.medical_card_expiry ? new Date(driver.medical_card_expiry) : null);
    setShowAddDialog(true);
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    
    try {
      await api.delete(`/drivers/${driverId}`);
      toast.success('Driver deleted');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to delete driver');
    }
  };

  const handleInviteDriver = async (driverId) => {
    setInvitingDriverId(driverId);
    try {
      const res = await api.post(`/driver/invite/${driverId}`);
      setInviteCode(res.data);
      setShowInviteDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInvitingDriverId(null);
    }
  };

  const copyInviteCode = () => {
    if (inviteCode?.invite_code) {
      navigator.clipboard.writeText(inviteCode.invite_code);
      toast.success('Invite code copied!');
    }
  };

  const handleUpdateStatus = async (driverId, status) => {
    try {
      await api.put(`/drivers/${driverId}`, { status });
      toast.success('Status updated');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">Available</span>;
      case 'on_load':
        return <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">On Load</span>;
      case 'off_duty':
        return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400">Off Duty</span>;
      default:
        return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400">{status}</span>;
    }
  };

  const getCertWarning = (daysUntil) => {
    if (daysUntil === null || daysUntil === undefined) return null;
    if (daysUntil < 0) return <span className="text-red-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Expired</span>;
    if (daysUntil <= 30) return <span className="text-yellow-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> {daysUntil}d</span>;
    return <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {daysUntil}d</span>;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#071423] pt-20 flex items-center justify-center">
        <Card className="max-w-md border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 mx-auto text-[#3B82F6] mb-4" />
            <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-gray-400 mb-4">Please sign in to manage your drivers.</p>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423] pt-20 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Users className="w-8 h-8 text-[#3B82F6]" />
              Driver Management
            </h1>
            <p className="text-gray-400 mt-2">
              Manage your drivers, track certifications, and monitor availability
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold" data-testid="add-driver-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Driver
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDriver ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
                <DialogDescription>
                  {editingDriver ? 'Update driver information' : 'Add a new driver to your fleet'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="555-123-4567"
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CDL Number</Label>
                    <Input
                      value={formData.cdl_number}
                      onChange={(e) => setFormData({ ...formData, cdl_number: e.target.value })}
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <div>
                    <Label>CDL State</Label>
                    <Select
                      value={formData.cdl_state}
                      onValueChange={(value) => setFormData({ ...formData, cdl_state: value })}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A1628] border-[#3B82F6]/20">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#112240] border-[#3B82F6]/20 max-h-60">
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CDL Expiry</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal bg-[#0A1628] border-[#3B82F6]/20">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {cdlExpiry ? format(cdlExpiry, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#112240] border-[#3B82F6]/20">
                        <Calendar mode="single" selected={cdlExpiry} onSelect={setCdlExpiry} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Medical Card Expiry</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal bg-[#0A1628] border-[#3B82F6]/20">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {medicalExpiry ? format(medicalExpiry, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#112240] border-[#3B82F6]/20">
                        <Calendar mode="single" selected={medicalExpiry} onSelect={setMedicalExpiry} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Equipment Type</Label>
                    <Select
                      value={formData.equipment_type}
                      onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A1628] border-[#3B82F6]/20">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                        {EQUIPMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Home Base</Label>
                    <Input
                      value={formData.home_base}
                      onChange={(e) => setFormData({ ...formData, home_base: e.target.value })}
                      placeholder="City, State"
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleSaveDriver} disabled={saving} className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (editingDriver ? 'Update' : 'Add Driver')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Drivers</p>
                  <p className="text-2xl font-bold text-white">{drivers.length}</p>
                </div>
                <Users className="w-8 h-8 text-[#3B82F6]" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Available</p>
                  <p className="text-2xl font-bold text-green-400">{drivers.filter(d => d.status === 'available').length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">On Load</p>
                  <p className="text-2xl font-bold text-blue-400">{drivers.filter(d => d.status === 'on_load').length}</p>
                </div>
                <Truck className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Cert. Expiring</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {drivers.filter(d => (d.days_until_cdl_expiry !== null && d.days_until_cdl_expiry <= 30) || (d.days_until_medical_expiry !== null && d.days_until_medical_expiry <= 30)).length}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Drivers List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : drivers.length === 0 ? (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="py-16 text-center">
              <User className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Drivers Yet</h3>
              <p className="text-gray-400 mb-6">Add your first driver to start managing your fleet.</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#4ADE80] hover:bg-[#22C55E] text-black">
                <Plus className="w-5 h-5 mr-2" /> Add Your First Driver
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {drivers.map((driver) => (
              <Card key={driver.id} className="border-[#3B82F6]/20 bg-[#0D1B2A] hover:border-[#3B82F6]/40 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                        <User className="w-6 h-6 text-[#3B82F6]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-3">
                          {driver.first_name} {driver.last_name}
                          {getStatusBadge(driver.status)}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {driver.phone}</span>
                          {driver.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {driver.email}</span>}
                          {driver.home_base && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {driver.home_base}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Certifications */}
                      <div className="text-right">
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-xs text-gray-500 block">CDL</span>
                            {getCertWarning(driver.days_until_cdl_expiry)}
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 block">Medical</span>
                            {getCertWarning(driver.days_until_medical_expiry)}
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="text-right border-l border-[#3B82F6]/20 pl-6">
                        <div className="text-lg font-bold text-white">{driver.total_loads}</div>
                        <div className="text-xs text-gray-500">loads</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#4ADE80]">${driver.total_revenue.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">revenue</div>
                      </div>

                      {/* Current Load */}
                      {driver.current_load && (
                        <div className="border-l border-[#3B82F6]/20 pl-6">
                          <div className="text-xs text-gray-500">Current Load</div>
                          <div className="text-sm text-white">{driver.current_load.pickup_location} → {driver.current_load.delivery_location}</div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleInviteDriver(driver.id)}
                          disabled={invitingDriverId === driver.id}
                          className="border-[#4ADE80]/30 text-[#4ADE80] hover:bg-[#4ADE80]/10"
                        >
                          {invitingDriverId === driver.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><LinkIcon className="w-4 h-4 mr-1" /> Invite</>
                          )}
                        </Button>
                        <Select
                          value={driver.status}
                          onValueChange={(value) => handleUpdateStatus(driver.id, value)}
                        >
                          <SelectTrigger className="w-[120px] bg-[#0A1628] border-[#3B82F6]/20 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="off_duty">Off Duty</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => handleEditDriver(driver)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDriver(driver.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Invite Code Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm">
            <DialogHeader>
              <DialogTitle>Driver Invite Code</DialogTitle>
              <DialogDescription>
                Share this code with {inviteCode?.driver_name} to let them set up their driver app account.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3">
              {/* Notification status - Email and SMS */}
              <div className="space-y-2">
                {/* Email status */}
                {inviteCode?.email_sent ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-400" />
                    <div className="flex-1">
                      <p className="text-xs text-green-400 font-medium">Email Sent</p>
                      <p className="text-[10px] text-gray-500">{inviteCode?.driver_email}</p>
                    </div>
                  </div>
                ) : inviteCode?.driver_email ? (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-yellow-400" />
                    <div className="flex-1">
                      <p className="text-xs text-yellow-400 font-medium">Email Failed</p>
                      <p className="text-[10px] text-gray-500">{inviteCode?.driver_email}</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <p className="text-xs text-gray-500">No email on file</p>
                  </div>
                )}
                
                {/* SMS status */}
                {inviteCode?.sms_sent ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-400" />
                    <div className="flex-1">
                      <p className="text-xs text-green-400 font-medium">SMS Sent</p>
                      <p className="text-[10px] text-gray-500">{inviteCode?.driver_phone}</p>
                    </div>
                  </div>
                ) : inviteCode?.driver_phone ? (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-yellow-400" />
                    <div className="flex-1">
                      <p className="text-xs text-yellow-400 font-medium">SMS Failed</p>
                      <p className="text-[10px] text-gray-500">{inviteCode?.driver_phone}</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-2.5 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <p className="text-xs text-gray-500">No phone on file</p>
                  </div>
                )}
              </div>
              
              {/* Show manual share notice if neither sent */}
              {!inviteCode?.email_sent && !inviteCode?.sms_sent && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2.5 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-blue-400" />
                  <p className="text-xs text-blue-400">Please share the code manually with your driver</p>
                </div>
              )}
              
              <div className="bg-[#112240] border border-[#4ADE80]/30 rounded-lg p-6 text-center">
                <p className="text-3xl font-mono font-bold text-[#4ADE80] tracking-widest">
                  {inviteCode?.invite_code}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Expires: {inviteCode?.expires_at ? new Date(inviteCode.expires_at).toLocaleDateString() : '7 days'}
                </p>
              </div>
              
              <Button onClick={copyInviteCode} className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black">
                <Copy className="w-4 h-4 mr-2" /> Copy Code
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-gray-400">
                  Driver app login: <span className="text-[#3B82F6]">/driver/login</span>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DriverManagementPage;
